import { useCallback, useState } from 'react';
import {
  Page,
  Layout,
  InlineGrid,
  Banner,
  Modal,
  Text,
  BlockStack,
  SkeletonBodyText,
} from '@shopify/polaris';
import { usePlans } from '../hooks/usePlans';
import { useSubscription } from '../hooks/useSubscription';
import { useUsage } from '../hooks/useUsage';
import { useThemeCompatibility } from '../hooks/useThemeCompatibility';
import { useAuthenticatedFetch } from '../hooks/useAuthenticatedFetch';
import { useLanguage } from '../providers/LanguageProvider';
import PlanCard from '../components/PlanCard';
import TrialQuotaBanner from '../components/TrialQuotaBanner';
import BillingProfileCard from '../components/BillingProfileCard';
import type { SubscribeResponse, ApiResponse, PageName } from '../types';
import { PlanTier, isSubscriptionActive } from '../types';

/** Try-ons the free trial allows, mirroring TRIAL_TRYON_CAP in the backend plan catalogue */
const TRIAL_TRYON_CAP = 50;

interface SubscriptionPageProps {
  onNavigate: (page: PageName) => void;
}

export default function SubscriptionPage(_props: SubscriptionPageProps) {
  const authenticatedFetch = useAuthenticatedFetch();
  const { plans, loading: plansLoading } = usePlans();
  const { subscription, loading: subLoading, refetch } = useSubscription();
  const { usage } = useUsage();
  const { compatible, themeName } = useThemeCompatibility();
  const { t } = useLanguage();
  const [subscribing, setSubscribing] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [freeEnrolled, setFreeEnrolled] = useState(false);
  const [billingProfileComplete, setBillingProfileComplete] = useState(false);
  const [billingProfileLoading, setBillingProfileLoading] = useState(true);

  const handleBillingProfileState = useCallback((complete: boolean, loading: boolean) => {
    setBillingProfileComplete(complete);
    setBillingProfileLoading(loading);
  }, []);

  const themeIncompatible = compatible === false;
  // The subscription record may exist as CANCELLED/FROZEN/PENDING after a decline
  // or uninstall — in that case the merchant has no current plan and every card
  // should show "Subscribe", not "Current Plan".
  const subscriptionIsActive = isSubscriptionActive(subscription);
  const effectiveCurrentPlanTier = subscriptionIsActive ? subscription?.planTier : undefined;
  // Quoted from the plan catalogue so the number can never drift from the backend.
  const freePlanTryOns =
    plans?.find((p) => p.id === PlanTier.FREE)?.includedTryOns ?? 0;

  const handleSubscribe = async (planTier: PlanTier) => {
    setError(null);
    setFreeEnrolled(false);
    if (planTier !== PlanTier.FREE && !billingProfileComplete) {
      setError(t.billingProfile.paidPlanBlocked);
      return;
    }

    setSubscribing(true);
    try {
      const response = await authenticatedFetch('/api/billing/subscribe', {
        method: 'POST',
        body: JSON.stringify({
          planTier,
        }),
      });

      const json: ApiResponse<SubscribeResponse> & {
        userErrors?: Array<{ field: string[]; message: string }>;
        action?: string;
      } = await response.json().catch(() => ({ success: false, error: `HTTP ${response.status}` }));

      if (!response.ok) {
        // Surface the most actionable message Shopify or our backend returned.
        const fromUserErrors = json.userErrors?.map((e) => e.message).join('; ');
        throw new Error(
          json.error === 'billing_profile_required'
            ? t.billingProfile.paidPlanBlocked
            : json.error || fromUserErrors || `HTTP error! status: ${response.status}`
        );
      }

      // The confirmation URL is a signed Shopify charge link. It is followed,
      // never rendered and never recorded: a replay frame containing one would
      // be a replayable charge approval.
      if (json.success && json.data?.confirmationUrl) {
        // Navigate the top-level frame to the Shopify charge approval page.
        // '_top' breaks out of the Shopify iframe; '_blank' is blocked as a popup in embedded apps.
        // The page navigates away here — refetch() is not called; the app reloads when
        // Shopify redirects back after the merchant approves or declines.
        window.open(json.data.confirmationUrl, '_top');
      } else if (json.success && planTier === PlanTier.FREE) {
        // The free plan carries no Shopify charge, so there is nothing to
        // approve: the backend applied it already and we just refresh.
        setFreeEnrolled(true);
        await refetch();
      } else {
        throw new Error(json.error || 'Failed to create subscription');
      }
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to subscribe';
      setError(message);
      console.error('Subscribe error:', err);
    } finally {
      setSubscribing(false);
    }
  };

  const handleCancelSubscription = async () => {
    setError(null);
    setShowCancelModal(false);

    try {
      const response = await authenticatedFetch('/api/billing/subscription', {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const json: ApiResponse<{ success: boolean }> = await response.json();

      if (json.success) {
        // Refresh subscription data
        await refetch();
      } else {
        throw new Error(json.error || 'Failed to cancel subscription');
      }
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to cancel subscription';
      setError(message);
      console.error('Cancel error:', err);
    }
  };

  const loading = plansLoading || subLoading;

  if (loading) {
    return (
      <Page title={t.subscription.title}>
        <Layout>
          <Layout.Section>
            <SkeletonBodyText lines={5} />
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  return (
    <Page
      title={t.subscription.title}
      secondaryActions={
        // There is nothing to cancel on the free plan: no charge, no commitment.
        subscriptionIsActive && effectiveCurrentPlanTier !== PlanTier.FREE
          ? [
              {
                content: t.subscription.cancelSubscription,
                destructive: true,
                onAction: () => setShowCancelModal(true),
              },
            ]
          : []
      }
    >
      <Layout>
        {/* Theme incompatibility warning */}
        {themeIncompatible && (
          <Layout.Section>
            <Banner title={t.themeCompatibility.incompatibleTitle} tone="critical">
              <p>{t.themeCompatibility.incompatibleSubscribeWarning}</p>
              {themeName && (
                <p>
                  <strong>{themeName}</strong>
                </p>
              )}
            </Banner>
          </Layout.Section>
        )}

        {/* Error Banner */}
        {error && (
          <Layout.Section>
            <Banner tone="critical" onDismiss={() => setError(null)}>
              {error}
            </Banner>
          </Layout.Section>
        )}

        {/* Trial running low, or spent: offer to start the plan now */}
        <TrialQuotaBanner usage={usage} />

        {/* Current Subscription Info */}
        {subscription && subscriptionIsActive && (
          <Layout.Section>
            <Banner tone="info">
              <p>
                {t.subscription.currentPlanBanner(subscription.planTier)}
                {t.subscription.subscriptionActive}
              </p>
            </Banner>
          </Layout.Section>
        )}
        {subscription && !subscriptionIsActive && (
          <Layout.Section>
            <Banner tone="warning">
              <p>
                {t.subscription.subscriptionInactiveBanner(
                  subscription.planTier,
                  subscription.status
                )}
              </p>
            </Banner>
          </Layout.Section>
        )}

        {/* Free plan confirmation */}
        {freeEnrolled && (
          <Layout.Section>
            <Banner
              title={t.subscription.freeEnrolled}
              tone="success"
              onDismiss={() => setFreeEnrolled(false)}
            >
              <p>{t.subscription.freeEnrolledBody(freePlanTryOns)}</p>
            </Banner>
          </Layout.Section>
        )}

        {/* Shopify owns the charge approval screen, so collect the merchant's
            legal invoice identity here before any paid confirmation URL exists. */}
        <Layout.Section>
          <BillingProfileCard onStateChange={handleBillingProfileState} />
        </Layout.Section>

        {/* Plan Cards */}
        <Layout.Section>
          <InlineGrid columns={{ xs: 1, sm: 2, md: 2, lg: 4 }} gap="400">
            {plans?.map((plan) => (
              <PlanCard
                key={plan.id}
                plan={plan}
                currentPlanTier={effectiveCurrentPlanTier}
                onSubscribe={handleSubscribe}
                loading={subscribing}
                disabled={
                  themeIncompatible ||
                  (plan.id !== PlanTier.FREE && (!billingProfileComplete || billingProfileLoading))
                }
                trialTryOnCap={TRIAL_TRYON_CAP}
              />
            ))}
          </InlineGrid>
        </Layout.Section>

        {/* How overage works, so the cap is never a surprise */}
        <Layout.Section>
          <Text as="p" variant="bodySm" tone="subdued">
            {t.subscription.overageExplainer}
          </Text>
        </Layout.Section>
      </Layout>

      {/* Cancel Confirmation Modal */}
      <Modal
        open={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        title={t.subscription.cancelModalTitle}
        primaryAction={{
          content: t.subscription.cancelSubscription,
          destructive: true,
          onAction: handleCancelSubscription,
        }}
        secondaryActions={[
          {
            content: t.subscription.keepSubscription,
            onAction: () => setShowCancelModal(false),
          },
        ]}
      >
        <Modal.Section>
          <BlockStack gap="400">
            <Text as="p" variant="bodyMd">
              {t.subscription.cancelModalBody}
            </Text>
            <ul style={{ marginLeft: '20px' }}>
              {t.subscription.cancelModalList.map((item, i) => (
                <li key={i}>{item}</li>
              ))}
            </ul>
            <Text as="p" variant="bodyMd">
              {t.subscription.cancelModalReactivate}
            </Text>
          </BlockStack>
        </Modal.Section>
      </Modal>
    </Page>
  );
}
